import * as storeTypes from "./storeTypes";
import { combineReducers } from "redux";
import IAppSessionState = storeTypes.IAppSessionState;
import { SessionState } from "../session/sessionState";
import { whistleBlowInitialState, whistleBlowReducer } from "./whistleBlow/storeWhistleBlow";
import { recordInitialState, recordReducer } from "./record/storeRecord";
import { eventInitialState, eventReducer } from "./event/storeEvent";
import { taskInitialState, taskReducer } from "./task/storeTask";
import { documentInitialState, documentReducer } from "./document/storeDocument";
import { companyInitialState, companyReducer } from "./company/storeCompany";
import { companyGroupInitialState, companyGroupReducer } from "./companyGroup/storeCompanyGroup";
import { userInitialState, userReducer } from "./user/storeUser";
import { userGroupInitialState, userGroupReducer } from "./userGroup/storeUserGroup";
import { logInitialState, logReducer } from "./log/storeLog";
import { instantMessageInitialState, instantMessageReducer } from "./instantMessage/storeInstantMessage";
import { messageInitialState, messageReducer } from "./message/storeMessage";
import { calendarTaskInitialState, calendarTaskReducer } from "./calendarTask/storeCalendarTask";
import { frontInitialState, frontReducer } from "./front/storeFront";
import { appReportInitialState, appReportReducer } from "./appReport/storeAppReport";
import { riskInitialState, riskReducer } from "./risk/storeRisk";

export const initialState: storeTypes.IApplicationState = {
    // Fetch
    fetchCount: 0,
    // Alert
    alert: {
        message: null,
        info: null,
        error: null
    },
    // Confirmation
    confirmation: {
        type: storeTypes.ConfirmationDialogType.Warning,
        title: null,
        text: null,
        show: false,
        yesConfirmationText: null,
        onYes: null,
        onNo: null,
        onCancel: null
    },
    // AutoLogout
    autoLogout: {
        show: false
    },
    // Session
    session: {
        state: SessionState.initialize(),
        newInstantMessages: []
    },
    // Front
    front: frontInitialState,
    // Documents
    document: documentInitialState,
    // Events
    event: eventInitialState,
    // Records
    record: recordInitialState,
    // Company
    company: companyInitialState,
    // CompanyGroup
    companyGroup: companyGroupInitialState,
    // User
    user: userInitialState,
    // UserGroups
    userGroup: userGroupInitialState,
    // Message
    message: messageInitialState,
    // Tasks
    task: taskInitialState,
    // Log
    log: logInitialState,
    // Instant Message
    instantMessage: instantMessageInitialState,
    // WhistleBlow
    whistleBlow: whistleBlowInitialState,
    // CalendarTask
    calendarTask: calendarTaskInitialState,
    // AppReport
    appReport: appReportInitialState,
    // Risk
    risk: riskInitialState
};

/* eslint-disable no-case-declarations */
// Fetch
function fetchCount(state: number = 0, action: storeTypes.FetchAction) {
    switch (action.type) {
    case storeTypes.FETCH_START:
        return state + 1;
    case storeTypes.FETCH_END:
        return state - 1;
    }
    return state;
}

// Alert
function alert(state: storeTypes.IAlertState = { message: "", info: "", error: "" }, action: storeTypes.AlertAction) {
    switch (action.type) {
    case storeTypes.ALERT_SUCCESS:
        return { ...state, message: (action as storeTypes.IAlertSuccess).message };
    case storeTypes.ALERT_INFO:
        return { ...state, info: (action as storeTypes.IAlertInfo).message };
    case storeTypes.ALERT_ERROR:
        return { ...state, error: (action as storeTypes.IAlertError).message };
    case storeTypes.ALERT_CLEAR:
        return { message: null, info: null, error: null };
    case storeTypes.ALERT_SUCCESS_CLEAR:
        return { ...state, message: null };
    case storeTypes.ALERT_INFO_CLEAR:
        return { ...state, info: null };
    }
    return state;
}

// Confirmation
function confirmation(state: storeTypes.IConfirmationState = { type: storeTypes.ConfirmationDialogType.Warning, title: "", text: "", show: false, onYes: null, onNo: null, onCancel: null, yesConfirmationText: null }, action: storeTypes.ConfirmationAction) {
    switch (action.type) {
    case storeTypes.CONFIRMATION_SET:
        const confirmation = (action as storeTypes.IConfirmationSet);
        return {
            ...state,
            type: confirmation.dialogType,
            title: confirmation.title,
            text: confirmation.text,
            show: confirmation.show,
            onYes: confirmation.onYes,
            onNo: confirmation.onNo,
            onCancel: confirmation.onCancel,
            yesConfirmationText: confirmation.yesConfirmationText
        };
    case storeTypes.CONFIRMATION_CLEAR:
        return { ...state, show: false };
    }
    return state;
}

// AutoLogout
function autoLogout(state: storeTypes.IAutoLogoutState = { show: false }, action: storeTypes.AutoLogoutAction) {
    switch (action.type) {
    case storeTypes.AUTOLOGOUT_SET:
        return { ...state, show: true };
    case storeTypes.AUTOLOGOUT_CLEAR:
        return { ...state, show: false };
    }
    return state;
}

// Session
function session(state: IAppSessionState = null, action: storeTypes.SessionAction) {
    switch (action.type) {
    case storeTypes.SESSION_SET_STATE:
        const setSessionState = (action as storeTypes.ISetSessionState);
        return { ...state, state: setSessionState.sessionState };
    case storeTypes.NEW_INSTANT_MESSAGES_SET:
        const setNewInstantMessages = (action as storeTypes.ISetNewInstantMessages);
        return { ...state, newInstantMessages: setNewInstantMessages.newInstantMessages };
    }
    return state;
}
/* eslint-enable no-case-declarations */

// Root reducer
export const reducer = combineReducers({
    fetchCount,
    alert,
    confirmation,
    autoLogout,
    session,
    front: frontReducer,
    document: documentReducer,
    event: eventReducer,
    record: recordReducer,
    company: companyReducer,
    companyGroup: companyGroupReducer,
    message: messageReducer,
    task: taskReducer,
    user: userReducer,
    userGroup: userGroupReducer,
    log: logReducer,
    instantMessage: instantMessageReducer,
    whistleBlow: whistleBlowReducer,
    calendarTask: calendarTaskReducer,
    appReport: appReportReducer,
    risk: riskReducer
});

export default reducer;